import { useTranslation } from "react-i18next";
import {
  useCreateActivityDraft,
  useDoesActivityHaveDraft,
} from "../../hooks/useActivities";
import { Activity, CreateActivityRequest } from "../../models/Activity";
import { Field } from "../../models/Field";
import { validateExistingItemForRequest } from "../../services/FieldService";
import { replaceImageSrcWithImageIds } from "../../services/ImageService";
import { ClientRoutes } from "../../models/ClientRoutes";
import { useNavigate } from "react-router-dom";
import { ButtonWithLoading } from "../ButtonWithLoading";
import { Button } from "@fluentui/react-components";

interface CreateDraftButtonProps {
  activity: Activity;
  activityFields: Field[];
}

export const CreateDraftButton = (props: CreateDraftButtonProps) => {
  const { activity, activityFields } = props;
  const { data: draftId, isLoading: loadingHasDraft } =
    useDoesActivityHaveDraft(activity.activityId ? activity.activityId : "");

  const { mutateAsync: createActivityDraft, isPending: createDraftPending } =
    useCreateActivityDraft();

  const { t } = useTranslation();

  const navigate = useNavigate();

  if (loadingHasDraft) {
    return <></>;
  }

  async function onCreateDraftClick() {
    if (!activity || !activity.activityId || !activityFields) {
      return;
    }

    const requestFields = validateExistingItemForRequest(
      activity.fieldValues,
      activityFields
    );

    // Convert base64 image src to imageIds using position from imageIdsWithProperties
    const serializedHtml = activity?.description?.serializedHtml
      ? replaceImageSrcWithImageIds(activity.description.serializedHtml)
      : activity?.description?.serializedHtml || "";

    const createRequest: CreateActivityRequest = {
      title: activity.title,
      serializedHtml: serializedHtml,
      fieldsWithValues: requestFields,
      activityId: activity.activityId,
      toolIds: activity.toolIds,
      mirroredActivityId: activity.mirroredActivityId,
      imageIds: activity.description?.imageIds,
      imageVersionIds: activity.description?.imageVersionIds,
      toolLinkIds: activity.description?.toolLinkIds,
      processFlowSettings: activity.processFlowSettings,
      processFlowHeadSettings: activity.processFlowHeadSettings,
    };

    const createdActivity = await createActivityDraft(createRequest);

    navigate(`${ClientRoutes.Activities}/${createdActivity.versionId}`);
  }

  const navigateToDraft = () => {
    if (!draftId) {
      return;
    }

    navigate(`${ClientRoutes.Activities}/${draftId}`);
  };

  return draftId ? (
    <Button onClick={navigateToDraft}>{t("toDraft")}</Button>
  ) : (
    <ButtonWithLoading
      onClick={onCreateDraftClick}
      isLoading={createDraftPending}
      text={t("edit")}
      disabled={!activity || !activity.activityId}
    />
  );
};
